'use strict'

// App time zone. Instants are stored in UTC. This module turns a wall-clock
// value in the app's time zone into that instant, and back again for display.

const DEFAULT_TIME_ZONE = 'UTC'

function isValidTimeZone (value) {

  if (!value || typeof value !== 'string') return false

  try {

    Intl.DateTimeFormat('en-US', { timeZone: value })

    return true
  } catch (error) {

    return false
  }
}

function normalizeTimeZone (value) {

  return isValidTimeZone(value) ? value : DEFAULT_TIME_ZONE
}

function timeZoneChoices () {

  if (typeof Intl.supportedValuesOf !== 'function') return [DEFAULT_TIME_ZONE]

  const zones = Intl.supportedValuesOf('timeZone')

  return zones.includes(DEFAULT_TIME_ZONE) ? zones : [DEFAULT_TIME_ZONE, ...zones]
}

function timeZoneLabel (zone, now) {

  const name = normalizeTimeZone(zone)

  try {

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: name,
      timeZoneName: 'shortOffset'
    }).formatToParts(now || new Date())
    const offset = parts.find(part => part.type === 'timeZoneName')

    return offset && offset.value ? `${name} (${offset.value})` : name
  } catch (error) {

    return name
  }
}

function zonedParts (date, timeZone) {

  const parts = {}
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date)

  for (const part of formatted) {

    if (part.type !== 'literal') parts[part.type] = part.value
  }

  if (parts.hour === '24') parts.hour = '00'

  return parts
}

function zoneOffsetMinutes (date, timeZone) {

  const parts = zonedParts(date, timeZone)
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  )

  return Math.round((asUtc - date.getTime()) / 60000)
}

function utcFromWallTime (value, timeZone) {

  const match = String(value == null ? '' : value).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/)

  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = match[4] == null ? 0 : Number(match[4])
  const minute = match[5] == null ? 0 : Number(match[5])
  const second = match[6] == null ? 0 : Number(match[6])
  const zone = normalizeTimeZone(timeZone)
  let utc = Date.UTC(year, month - 1, day, hour, minute, second)

  for (let pass = 0; pass < 3; pass++) {

    const offset = zoneOffsetMinutes(new Date(utc), zone)

    utc = Date.UTC(year, month - 1, day, hour, minute, second) - (offset * 60000)
  }

  return new Date(utc).toISOString()
}

function instantDate (value) {

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  if (value == null || value === '') return null

  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? null : date
}

function storeDateTime (value, timeZone) {

  if (value == null || value === '') return null

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString()

  const text = String(value).trim()

  if (!text) return null

  if (/[zZ]$/.test(text) || /[+-]\d{2}:\d{2}$/.test(text)) {

    const date = new Date(text)

    return Number.isNaN(date.getTime()) ? text : date.toISOString()
  }

  return utcFromWallTime(text, timeZone) || text
}

function browserTimeZone () {

  try {

    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone

    return isValidTimeZone(zone) ? zone : DEFAULT_TIME_ZONE
  } catch (error) {

    return DEFAULT_TIME_ZONE
  }
}

function isCalendarDate (value) {

  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
}

function isDateOnlyColumn (column) {

  if (!column || column.type !== 'dateTime') return false

  return column.dateOnly === true || column.dateOnly === 'true' || column.dateOnly === 1
}

function dateOnlyFields (columnType, source, previous) {

  if (columnType !== 'dateTime') return { dateOnly: false }

  if (source && Object.prototype.hasOwnProperty.call(source, 'dateOnly')) {

    return { dateOnly: source.dateOnly === true || source.dateOnly === 'true' }
  }

  return { dateOnly: !!(previous && previous.dateOnly) }
}

function storeDateOnly (value) {

  if (value == null || value === '') return null

  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/)

  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const probe = new Date(Date.UTC(year, month - 1, day))

  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null

  return `${match[1]}-${match[2]}-${match[3]}`
}

function formatCalendarDate (value) {

  const day = storeDateOnly(value)

  if (!day) return value == null ? '' : String(value)

  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12))

  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date)
}

function formatClock (value, timeZone) {

  const date = instantDate(value)

  if (!date) return ''

  return new Intl.DateTimeFormat(undefined, {
    timeZone: normalizeTimeZone(timeZone),
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

function todayInZone (timeZone, now) {

  return calendarDayInTimeZone(now || new Date(), timeZone)
}

function clockInZone (dailyTime, sourceZone, targetZone, now) {

  const match = String(dailyTime || '').match(/^(\d{1,2}):(\d{2})/)

  if (!match) return ''

  const parts = zonedParts(now || new Date(), sourceZone)
  const hour = String(match[1]).padStart(2, '0')
  const instant = utcFromWallTime(`${parts.year}-${parts.month}-${parts.day}T${hour}:${match[2]}`, sourceZone)

  return formatClock(instant, targetZone)
}

function importTimeZoneProblem (columns, rows, timeZone) {

  const dated = (columns || []).filter((column) => column && column.type === 'dateTime' && !isDateOnlyColumn(column))

  if (!dated.length) return null

  const hasValue = dated.some((column) => (rows || []).some((row) => {

    const value = row && row[column.uniqueTitle]

    return value != null && String(value).trim() !== ''
  }))

  if (!hasValue) return null

  if (!isValidTimeZone(timeZone)) return 'Import needs the time zone of the file for date and time columns.'

  return null
}

function calendarDayInTimeZone (value, timeZone) {

  if (isCalendarDate(value)) return String(value).trim()

  const date = instantDate(value)

  if (!date) return null

  const parts = zonedParts(date, timeZone)

  return `${parts.year}-${parts.month}-${parts.day}`
}

function toDateTimeLocal (value, timeZone) {

  const date = instantDate(value)

  if (!date) return ''

  const parts = zonedParts(date, timeZone)

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

function formatInstant (value, timeZone) {

  if (isCalendarDate(value)) return formatCalendarDate(value)

  const date = instantDate(value)

  if (!date) return value == null ? '' : String(value)

  return new Intl.DateTimeFormat(undefined, {
    timeZone: normalizeTimeZone(timeZone),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

function formatStoredInstant (value, timeZone) {

  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(value)) return value

  const formatted = formatInstant(value, timeZone)

  return formatted || value
}

module.exports = {
  DEFAULT_TIME_ZONE,
  isValidTimeZone,
  normalizeTimeZone,
  timeZoneChoices,
  timeZoneLabel,
  browserTimeZone,
  isCalendarDate,
  isDateOnlyColumn,
  dateOnlyFields,
  storeDateOnly,
  formatCalendarDate,
  formatClock,
  todayInZone,
  clockInZone,
  importTimeZoneProblem,
  utcFromWallTime,
  storeDateTime,
  calendarDayInTimeZone,
  toDateTimeLocal,
  formatInstant,
  formatStoredInstant
}
